import { createEffect, type JSX, ErrorBoundary as SolidErrorBoundary } from "solid-js";
import { msg } from "@lingui/core/macro";
import { RefreshCw, RotateCcw } from "@fleet/icons";
import { GlassSurface } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@time-tracker/shared";

/**
 * Theme-aware "broken hourglass" illustration for crash fallbacks. Leans on
 * `currentColor` + the destructive token so it adapts to every theme. Purely
 * decorative (`aria-hidden`); the copy carries the meaning.
 */
function CrashIllustration(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden="true"
      class={cn("text-destructive", props.class)}
    >
      <circle cx="80" cy="80" r="58" fill="currentColor" opacity="0.06" />
      <circle cx="80" cy="80" r="46" fill="currentColor" opacity="0.05" />
      {/* Hourglass frame */}
      <g stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <line x1="56" y1="46" x2="104" y2="46" opacity="0.6" />
        <line x1="56" y1="114" x2="104" y2="114" opacity="0.6" />
        <path d="M60 46 L60 60 Q60 72 80 80 Q60 88 60 100 L60 114" opacity="0.5" />
        <path d="M100 46 L100 60 Q100 72 80 80 Q100 88 100 100 L100 114" opacity="0.5" />
      </g>
      {/* Spilled / fractured sand */}
      <g fill="currentColor">
        <path d="M68 56 L92 56 L80 70 Z" opacity="0.45" />
        <circle cx="74" cy="104" r="3" opacity="0.4" />
        <circle cx="86" cy="106" r="2.5" opacity="0.35" />
        <circle cx="80" cy="98" r="2.5" opacity="0.5" />
      </g>
      {/* Crack across the bulb */}
      <path
        d="M70 74 L78 80 L72 85 L82 90"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        opacity="0.85"
      />
    </svg>
  );
}

interface ErrorFallbackProps {
  error: unknown;
  reset: () => void;
  /** Compact variant for inline region boundaries (no full-screen min-height). */
  variant?: "full" | "region";
  /** Optional override for the headline (e.g. region-specific). */
  title?: string;
  /** Optional override for the description. */
  description?: string;
}

/** Premium, theme-aware crash card used by every error boundary. */
function ErrorFallback(props: ErrorFallbackProps) {
  const { t } = useI18n();

  const message = () =>
    props.error instanceof Error ? props.error.message : t(msg`An unexpected error has occurred.`);

  return (
    <div
      class={cn(
        "flex w-full items-center justify-center p-4",
        props.variant === "region" ? "py-12" : "min-h-dvh",
      )}
      role="alert"
    >
      <GlassSurface class="flex w-full max-w-md flex-col items-center space-y-6 p-8 text-center">
        <CrashIllustration class="h-28 w-28" />
        <div class="space-y-2">
          <h2 class="text-xl font-bold tracking-tight">
            {props.title ?? t(msg`Something went wrong`)}
          </h2>
          <p class="text-sm text-muted-foreground">
            {props.description ?? t(msg`This view ran into an unexpected problem.`)}
          </p>
          <p class="break-words text-xs text-muted-foreground/60">{message()}</p>
        </div>
        <div class="flex w-full gap-3">
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Reload`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RotateCcw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: JSX.Element;
  variant?: "full" | "region";
  title?: string;
  description?: string;
  /** Context label used in the console log to locate the failing region. */
  label?: string;
}

/**
 * Solid `<ErrorBoundary>` wrapper with a premium, theme-aware fallback. Catches
 * render-time errors anywhere in the subtree so the user never sees a blank
 * screen. `reset()` re-runs the subtree; "Reload" hard-reloads the document.
 */
export function AppErrorBoundary(props: AppErrorBoundaryProps) {
  return (
    <SolidErrorBoundary
      fallback={(error: unknown, reset: () => void) => {
        createEffect(() => {
          console.error(`[AppErrorBoundary${props.label ? `:${props.label}` : ""}]`, error);
        });
        return (
          <ErrorFallback
            error={error}
            reset={reset}
            variant={props.variant}
            title={props.title}
            description={props.description}
          />
        );
      }}
    >
      {props.children}
    </SolidErrorBoundary>
  );
}
